import type React from 'react'

import { ProviderInNetworkVerificationTakeover } from './components/ProviderInNetworkVerificationTakeover'
import { getStoredHistory, selectedPlansFromSmartBudget } from './navigationUtils'
import { AdditionalDetailsPage } from './pages/AdditionalDetailsPage'
import { ConfirmAndEnrollPage } from './pages/ConfirmAndEnrollPage'
import { DentalPlansPage } from './pages/DentalPlansPage'
import { GenerateAllowancesPage } from './pages/GenerateAllowancesPage'
import {
  MedicalPlanDetailPage,
  RenewalCurrMedicalPlanDetailPage,
  RenewalNextMedicalPlanDetailPage,
} from './pages/MedicalPlanDetailPage'
import { MedicalPlansComparePage } from './pages/MedicalPlansComparePage'
import { MedicalPlansPageRouter } from './pages/MedicalPlansPageRouter'
import {
  OptedOutFromSubsidyEligiblePage,
  OptedOutPage,
} from './pages/OptedOutPage'
import { PhoneVerificationPage } from './pages/PhoneVerificationPage'
import { RenewalConfirmAndEnrollPage } from './pages/RenewalConfirmAndEnrollPage'
import { RenewalConfirmInfoPage } from './pages/RenewalConfirmInfoPage'
import { RenewalShowSummaryPage } from './pages/RenewalShowSummaryPage'
import { SmartBudgetPage } from './pages/SmartBudgetPage'
import { StateFormPage } from './pages/StateFormPage'
import { SubsidyEligiblePage } from './pages/SubsidyEligiblePage'
import { ViewAllowancesPage } from './pages/ViewAllowancesPage'
import { VisionPlansPage } from './pages/VisionPlansPage'
import {
  type OnboardingInfo,
  type Step,
  type StepComponentProps,
} from './types'
import { allowanceIsEmpty, isRenewal } from './utils'

const phoneNumberNeedsToBeVerified = (data: OnboardingInfo) => {
  return (
    data.restrictions?.phone_number_verification ===
    'phone_number_verification_required'
  )
}
const phoneNumberVerified = (data: OnboardingInfo) => {
  return !data.restrictions?.phone_number_verification
}
const memberAllowanceMissingFieldsAreEmpty = (data: OnboardingInfo) => {
  const allowanceFields = data.missing_fields.allowance
  if (!allowanceFields || allowanceFields.length === 0) return true
  return allowanceFields.every((field) => {
    return !field.startsWith('member.')
  })
}
const participantsAllowanceMissingFieldsAreEmpty = (data: OnboardingInfo) => {
  const allowanceFields = data.missing_fields.allowance
  if (!allowanceFields || allowanceFields.length === 0) return true
  return allowanceFields.every((field) => {
    return !field.startsWith('additional_participants.')
  })
}
const enrollmentMissingFieldsAreEmpty = (data: OnboardingInfo) => {
  const enrollmentFields = data.missing_fields.enrollment
  if (!enrollmentFields || enrollmentFields.length === 0) return true
  return false
}
const memberIsMissingStateEnrollmentFields = (data: OnboardingInfo) => {
  const enrollmentFields = data.missing_fields.enrollment
  if (!enrollmentFields || enrollmentFields.length === 0) return false
  return enrollmentFields.some((field) => {
    return field === 'state_config'
  })
}
const dentalPlanSelected = (data: OnboardingInfo) => {
  const enrollments = data.enrollments
  if (!enrollments || !enrollments.length) return false
  return (
    enrollments.filter((enrollment) => enrollment.covers_dental).length > 0
  )
}
const visionPlanSelected = (data: OnboardingInfo) => {
  const enrollments = data.enrollments
  if (!enrollments || !enrollments.length) return false
  return (
    enrollments.filter((enrollment) => enrollment.covers_vision).length > 0
  )
}
const medicalPlanSelected = (data: OnboardingInfo) => {
  const enrollments = data.enrollments
  if (!enrollments || !enrollments.length) return false

  const medicalEnrollments = enrollments.filter(
    (enrollment) => enrollment.covers_medical
  )
  if (medicalEnrollments.length === 0) return false

  if (
    medicalEnrollments.some(
      (enrollment) => enrollment.payment_method !== 'paid_by_thatch'
    )
  ) {
    // if the medical enrollment is off thatch then the POC should have been uploaded
    return !data.missing_fields.enrollment?.includes(
      'off_thatch_proof_of_coverage'
    )
  } else {
    return true
  }
}
const failedToGenerateAllowance = (data: OnboardingInfo) => {
  return data.restrictions?.allowance_generation_failed
}
const additionalDetailsMissing = (data: OnboardingInfo) => {
  return (
    data.missing_fields.plans_selection &&
    data.missing_fields.plans_selection.length > 0
  )
}
const eligibleForSubsidy = (data: OnboardingInfo) => {
  return (
    !additionalDetailsMissing(data) &&
    !allowanceIsEmpty(data.allowance) &&
    !!data.restrictions?.subsidy_eligible
  )
}
const memberMissingCounty = (data: OnboardingInfo) => {
  return data.restrictions?.member_missing_county
}
const stepAfterMedicalPlan = (data: OnboardingInfo | null | undefined) => {
  if (data && !data?.restrictions?.dental_not_available) {
    return 'dental_plan'
  }
  if (data && !data?.restrictions?.vision_not_available) {
    return 'vision_plan'
  }
  if (data?.state_config) {
    return 'state_form'
  }
  if (data && isRenewal(data)) {
    return 'renewal_confirm_enrollment'
  }
  return 'finalize_enrollment'
}
const stepAfterDentalPlan = (data: OnboardingInfo | null | undefined) => {
  if (data && !data?.restrictions?.vision_not_available) {
    return 'vision_plan'
  }
  if (data?.state_config) {
    return 'state_form'
  }
  if (data && isRenewal(data)) {
    return 'renewal_confirm_enrollment'
  }
  return 'finalize_enrollment'
}
const stepAfterVisionPlan = (data: OnboardingInfo | null | undefined) => {
  if (data?.state_config) {
    return 'state_form'
  }
  if (data && isRenewal(data)) {
    return 'renewal_confirm_enrollment'
  }
  return 'finalize_enrollment'
}
const stepBeforeVisionPlan = (data: OnboardingInfo | null | undefined) => {
  if (data && !data?.restrictions?.dental_not_available) {
    return 'dental_plan'
  }
  return 'medical_plan'
}
const lastPlanSelectionStep = (
  data: OnboardingInfo | null | undefined,
  enrollmentEventId?: string
) => {
  // Check if user came from smart_budget
  if (selectedPlansFromSmartBudget(enrollmentEventId)) {
    return 'smart_budget'
  }

  // Default logic for traditional flow
  if (data && !data?.restrictions?.vision_not_available) {
    return 'vision_plan'
  } else if (data && !data?.restrictions?.dental_not_available) {
    return 'dental_plan'
  }
  return 'medical_plan'
}
const stepBeforeFinalizeEnrollment = (
  data: OnboardingInfo | null | undefined,
  enrollmentEventId?: string
) => {
  if (data?.state_config) {
    return 'state_form'
  }
  return lastPlanSelectionStep(data, enrollmentEventId)
}
const hasCompletedPlanSelectionPrerequisites = (data: OnboardingInfo) => {
  return (
    memberAllowanceMissingFieldsAreEmpty(data) &&
    participantsAllowanceMissingFieldsAreEmpty(data) &&
    !allowanceIsEmpty(data.allowance) &&
    phoneNumberVerified(data) &&
    !additionalDetailsMissing(data) &&
    !memberMissingCounty(data)
  )
}
const hasSelectedAllPlans = (data: OnboardingInfo) => {
  if ((data.missing_fields.enrollment || []).includes('medical_enrollment')) {
    return false
  }

  // Note: currently we are not producing any enrollments for medicare, so we can assume that the
  // condition above would have filtered out missing required medical enrollment cases
  const medicalEnrollments = data.enrollments?.filter(
    (enrollment) => enrollment.covers_medical
  )
  if (
    medicalEnrollments?.some(
      (enrollment) => enrollment.payment_method !== 'paid_by_thatch'
    )
  ) {
    // if the medical enrollment is off thatch then the POC should have been uploaded
    return !data.missing_fields.enrollment?.includes(
      'off_thatch_proof_of_coverage'
    )
  }
  return true
}

/* Logic needed to get the step based on the data returned */
export const getStepFromData = (data: OnboardingInfo): Step | null => {
  const eligibleForSubsidyResult = eligibleForSubsidy(data)
  // Handle opted out case first
  if (data.progress === 'opted_out') {
    if (eligibleForSubsidyResult) {
      return 'opted_out_from_subsidy_eligible'
    }
    return 'opted_out'
  } else if (data.progress === 'completed') {
    return 'completed'
  } else if (data.progress === 'incomplete') {
    const isRenewalFlow = isRenewal(data)
    const allowanceIsEmptyResult = allowanceIsEmpty(data.allowance)

    // If this is a renewal flow, we always start with the confirm info page
    // TODO: or should we have some kind of indication that the user has reviewed their information
    // at least once?
    if (isRenewalFlow) {
      return 'renewal_confirm_info'
    }

    // Check if we need to generate allowances or verify phone
    if (
      (allowanceIsEmptyResult &&
        (data.missing_fields.allowance?.length ||
          failedToGenerateAllowance(data))) ||
      phoneNumberNeedsToBeVerified(data) ||
      memberMissingCounty(data)
    ) {
      return 'generate_allowances'
    }

    // Check if we have completed all allowance prerequisites
    const hasCompletedAllowancePrerequisites =
      memberAllowanceMissingFieldsAreEmpty(data) &&
      participantsAllowanceMissingFieldsAreEmpty(data) &&
      !allowanceIsEmptyResult &&
      phoneNumberVerified(data) &&
      !memberMissingCounty(data)

    if (!hasCompletedAllowancePrerequisites) {
      // If we haven't completed all prerequisites but have basic allowance info, show view_allowances
      if (
        memberAllowanceMissingFieldsAreEmpty(data) &&
        !allowanceIsEmptyResult &&
        phoneNumberVerified(data) &&
        !memberMissingCounty(data)
      ) {
        return 'view_allowances'
      }
      // If we don't have basic allowance info, we need to generate allowances
      return 'generate_allowances'
    }

    // At this point we know all allowance prerequisites are met
    if (additionalDetailsMissing(data)) {
      return 'additional_details'
    }

    if (eligibleForSubsidyResult) {
      return 'subsidy_eligible'
    }

    // Handle plan selection flow
    if (!medicalPlanSelected(data)) {
      return 'medical_plan'
    }

    if (!data.restrictions?.dental_not_available && !dentalPlanSelected(data)) {
      return 'dental_plan'
    }

    if (!data.restrictions?.vision_not_available && !visionPlanSelected(data)) {
      return 'vision_plan'
    }

    if (data.state_config && memberIsMissingStateEnrollmentFields(data)) {
      return 'state_form'
    }

    // if the member passed all the checks above, we can assume they are ready to finalize their enrollment
    // regardless of having missing enrollment fields or not
    return 'finalize_enrollment'
  }

  return null
}

const extractPlanIds = (step: string): string[] | null => {
  if (
    step.startsWith('medical_plan_details:') ||
    step.startsWith('medical_plan_comparison:') ||
    step.startsWith('in_network_verification:') ||
    step.startsWith('renewal_curr_year_medical_plan_details:') ||
    step.startsWith('renewal_next_year_medical_plan_details:')
  ) {
    const returnValue = step.split(':')[1].split(',')
    if (returnValue.length === 1 && returnValue[0] === '') {
      return null
    }
    return returnValue
  }
  return null
}

export const getStepMetadata = (
  step: Step | null
): Record<string, any> | undefined => {
  if (!step) return undefined
  if (typeof step === 'string' && step.startsWith('medical_plan_details:')) {
    return { planIds: extractPlanIds(step) }
  }
  if (typeof step === 'string' && step.startsWith('in_network_verification:')) {
    return { planIds: extractPlanIds(step) }
  }
  if (typeof step === 'string' && step.startsWith('medical_plan_comparison:')) {
    return { planIds: extractPlanIds(step) }
  }
  if (
    typeof step === 'string' &&
    step.startsWith('renewal_curr_year_medical_plan_details:')
  ) {
    return { planIds: extractPlanIds(step) }
  }
  if (
    typeof step === 'string' &&
    step.startsWith('renewal_next_year_medical_plan_details:')
  ) {
    return { planIds: extractPlanIds(step) }
  }
  return undefined
}

export const getBaseStep = (step: Step): Step => {
  if (typeof step === 'string' && step.startsWith('medical_plan_details:')) {
    return 'medical_plan_details'
  }
  if (typeof step === 'string' && step.startsWith('medical_plan_comparison:')) {
    return 'medical_plan_comparison'
  }
  if (typeof step === 'string' && step.startsWith('in_network_verification:')) {
    return 'in_network_verification'
  }
  if (
    typeof step === 'string' &&
    step.startsWith('renewal_curr_year_medical_plan_details:')
  ) {
    return 'renewal_curr_year_medical_plan_details'
  }
  if (
    typeof step === 'string' &&
    step.startsWith('renewal_next_year_medical_plan_details:')
  ) {
    return 'renewal_next_year_medical_plan_details'
  }
  return step
}

export const componentMappings: Partial<
  Record<
    Step,
    {
      component: (props: StepComponentProps) => React.JSX.Element;
      canShow: (
        data: OnboardingInfo,
        metadata?: Record<string, any>,
        featureFlags?: { [key: string]: boolean },
      ) => boolean;
      isCompleted?: (data: OnboardingInfo) => boolean;
      previousStep: (
        data?: OnboardingInfo | null,
        enrollmentEventId?: string
      ) => Step | null;
      nextStep: (data?: OnboardingInfo | null) => Step | null;
      layout: 'full' | 'no_sidebar' | 'sidebar';
    }
  >
> = {
  generate_allowances: {
    component: GenerateAllowancesPage,
    canShow: () => true,
    previousStep: () => null,
    nextStep: (data) => {
      if (data && phoneNumberNeedsToBeVerified(data)) {
        return 'phone_verification'
      }
      return 'view_allowances'
    },
    layout: 'no_sidebar',
  },
  phone_verification: {
    component: PhoneVerificationPage,
    canShow: (data) =>
      memberAllowanceMissingFieldsAreEmpty(data) &&
      phoneNumberNeedsToBeVerified(data) &&
      !failedToGenerateAllowance(data) &&
      !memberMissingCounty(data),
    previousStep: (data) =>
      data && isRenewal(data) ? 'renewal_confirm_info' : 'generate_allowances',
    nextStep: (data) =>
      data && isRenewal(data) ? 'renewal_show_summary' : 'view_allowances',
    layout: 'no_sidebar',
  },
  view_allowances: {
    component: ViewAllowancesPage,
    canShow: (data) =>
      memberAllowanceMissingFieldsAreEmpty(data) &&
      !allowanceIsEmpty(data.allowance) &&
      phoneNumberVerified(data) &&
      !memberMissingCounty(data),
    previousStep: () => 'generate_allowances',
    nextStep: (data) => {
      if (data && isRenewal(data)) {
        return 'renewal_show_summary'
      } else if (
        data?.restrictions?.medicare_eligible &&
        data.restrictions?.medicare_eligible?.length > 0
      ) {
        return 'medical_plan'
      }
      return 'additional_details'
    },
    layout: 'no_sidebar',
  },
  additional_details: {
    component: AdditionalDetailsPage,
    canShow: (data) =>
      memberAllowanceMissingFieldsAreEmpty(data) &&
      participantsAllowanceMissingFieldsAreEmpty(data) &&
      !allowanceIsEmpty(data.allowance) &&
      phoneNumberVerified(data) &&
      !memberMissingCounty(data) &&
      !data.restrictions?.medicare_eligible,
    isCompleted: (data) => data.missing_fields.plans_selection?.length === 0,
    previousStep: () => 'view_allowances',
    nextStep: (data) => {
      if (data && eligibleForSubsidy(data)) {
        return 'subsidy_eligible'
      }
      return 'medical_plan'
    },
    layout: 'sidebar',
  },
  medical_plan: {
    component: MedicalPlansPageRouter,
    canShow: (data) =>
      !allowanceIsEmpty(data.allowance) &&
      data.missing_fields.plans_selection?.length === 0,
    isCompleted: (data) =>
      medicalPlanSelected(data) ||
      (data.missing_fields.plans_selection?.length === 0 &&
        (!isRenewal(data) && !!data.restrictions?.medicare_eligible)),
    previousStep: (data) =>
      data && isRenewal(data) ? 'renewal_show_summary' : 'additional_details',
    nextStep: (data) => stepAfterMedicalPlan(data),
    layout: 'sidebar',
  },
  medical_plan_details: {
    component: MedicalPlanDetailPage,
    canShow: (data, metadata) => {
      return (
        data.missing_fields.plans_selection?.length === 0 &&
        metadata?.planIds?.length > 0
      )
    },
    previousStep: () => 'medical_plan',
    nextStep: (data) => stepAfterMedicalPlan(data),
    layout: 'no_sidebar',
  },
  in_network_verification: {
    component: ProviderInNetworkVerificationTakeover,
    canShow: (_data, metadata) => {
      return (
        metadata?.planIds?.length > 0
      )
    },
    previousStep: () => 'medical_plan',
    nextStep: () => 'medical_plan',
    layout: 'full',
  },
  medical_plan_comparison: {
    component: MedicalPlansComparePage,
    canShow: (data, metadata) => {
      return (
        data.missing_fields.plans_selection?.length === 0 &&
        metadata?.planIds?.length > 0
      )
    },
    previousStep: () => 'medical_plan',
    nextStep: (data) => stepAfterMedicalPlan(data),
    layout: 'full',
  },
  smart_budget: {
    component: SmartBudgetPage,
    canShow: (data, _metadata, featureFlags) => {
      return (
        (featureFlags?.showSmartBudget ?? false) &&
        !allowanceIsEmpty(data.allowance) &&
        data.missing_fields.plans_selection?.length === 0
      )
    },
    isCompleted: (data) =>
      hasCompletedPlanSelectionPrerequisites(data) && hasSelectedAllPlans(data),
    previousStep: () => 'medical_plan',
    nextStep: (data) => stepAfterVisionPlan(data),
    layout: 'no_sidebar',
  },
  dental_plan: {
    component: DentalPlansPage,
    canShow: (data) =>
      data.missing_fields.plans_selection?.length === 0 &&
      !data.restrictions?.dental_not_available,
    isCompleted: (data) =>
      !data.restrictions?.dental_not_available && dentalPlanSelected(data),
    previousStep: () => 'medical_plan',
    nextStep: (data) => stepAfterDentalPlan(data),
    layout: 'sidebar',
  },
  vision_plan: {
    component: VisionPlansPage,
    canShow: (data) =>
      data.missing_fields.plans_selection?.length === 0 &&
      !data.restrictions?.vision_not_available,
    isCompleted: (data) =>
      !data.restrictions?.vision_not_available && visionPlanSelected(data),
    previousStep: (data) => stepBeforeVisionPlan(data),
    nextStep: (data) => stepAfterVisionPlan(data),
    layout: 'sidebar',
  },
  state_form: {
    component: StateFormPage,
    canShow: (data) =>
      hasCompletedPlanSelectionPrerequisites(data) &&
      hasSelectedAllPlans(data) &&
      !!data.state_config,
    isCompleted: (data) =>
      !!data.missing_fields.enrollment &&
      !data.missing_fields.enrollment?.includes('state_config'),
    previousStep: (data, enrollmentEventId) => lastPlanSelectionStep(data, enrollmentEventId),
    nextStep: (data) =>
      data && isRenewal(data)
        ? 'renewal_confirm_enrollment'
        : 'finalize_enrollment',
    layout: 'sidebar',
  },
  finalize_enrollment: {
    component: ConfirmAndEnrollPage,
    canShow: (data) =>
      hasCompletedPlanSelectionPrerequisites(data) && hasSelectedAllPlans(data),
    isCompleted: (data) => enrollmentMissingFieldsAreEmpty(data),
    previousStep: (data, enrollmentEventId) => stepBeforeFinalizeEnrollment(data, enrollmentEventId),
    nextStep: () => null, // terminal step
    layout: 'sidebar',
  },
  opted_out: {
    component: OptedOutPage,
    canShow: (data) =>
      !allowanceIsEmpty(data.allowance) || data.progress === 'opted_out',
    // This is a special case where we can't really determine what the previous step is supposed to be
    // We read from the localStorage to see what the previous page was (based on the navigation history)
    // or get the step from the data in case sessionStorage got deleted in the meantime
    previousStep: (data, enrollmentEventId) => {
      const storedHistory = enrollmentEventId
        ? getStoredHistory(enrollmentEventId)
        : []
      let previousStep = null
      if (storedHistory.length >= 2) {
        // 2 = at least one current step (which would be OptedOutPage, and at least one previous step)
        previousStep = storedHistory[storedHistory.length - 2]
        return previousStep as Step
      } else {
        // If we can't find previous step from the navigation history, we deduce it from the onboarding data
        return data ? getStepFromData(data) : null
      }
    },
    nextStep: () => null, // terminal step
    layout: 'full',
  },
  opted_out_from_subsidy_eligible: {
    component: OptedOutFromSubsidyEligiblePage,
    canShow: (data) => eligibleForSubsidy(data),
    previousStep: () => 'subsidy_eligible',
    nextStep: () => null, // terminal step
    layout: 'full',
  },
  subsidy_eligible: {
    component: SubsidyEligiblePage,
    canShow: (data) => eligibleForSubsidy(data),
    previousStep: (data) => {
      if (data && isRenewal(data)) {
        return 'renewal_confirm_info'
      }
      return 'additional_details'
    },
    nextStep: (data) => {
      if (data && isRenewal(data)) {
        return 'renewal_show_summary'
      }
      return 'medical_plan'
    },
    layout: 'sidebar',
  },
  // Renewal specific steps
  renewal_confirm_info: {
    component: RenewalConfirmInfoPage,
    canShow: (data) => isRenewal(data),
    previousStep: () => null,
    nextStep: (data) => {
      if (data && phoneNumberNeedsToBeVerified(data)) {
        return 'phone_verification'
      } else if (data && eligibleForSubsidy(data)) {
        return 'subsidy_eligible'
      }
      return 'renewal_show_summary'
    },
    layout: 'no_sidebar',
  },
  renewal_show_summary: {
    component: RenewalShowSummaryPage,
    canShow: (data) =>
      isRenewal(data) &&
      memberAllowanceMissingFieldsAreEmpty(data) &&
      participantsAllowanceMissingFieldsAreEmpty(data) &&
      !allowanceIsEmpty(data.allowance) &&
      phoneNumberVerified(data) &&
      !memberMissingCounty(data),
    previousStep: () => 'renewal_confirm_info',
    nextStep: (data) => {
      if (data?.state_config && memberIsMissingStateEnrollmentFields(data)) {
        return 'state_form'
      }
      return 'renewal_confirm_enrollment'
    },
    layout: 'no_sidebar',
  },
  renewal_confirm_enrollment: {
    component: RenewalConfirmAndEnrollPage,
    canShow: (data) =>
      isRenewal(data) &&
      hasCompletedPlanSelectionPrerequisites(data) &&
      hasSelectedAllPlans(data),
    isCompleted: (data) =>
      enrollmentMissingFieldsAreEmpty(data) && data.progress === 'completed',
    previousStep: (data, enrollmentEventId) => stepBeforeFinalizeEnrollment(data, enrollmentEventId),
    nextStep: () => null, // terminal step
    layout: 'sidebar',
  },
  renewal_curr_year_medical_plan_details: {
    component: RenewalCurrMedicalPlanDetailPage,
    canShow: (data, metadata) => {
      return (
        data.missing_fields.plans_selection?.length === 0 &&
        metadata?.planIds?.length > 0 &&
        isRenewal(data)
      )
    },
    previousStep: () => null,
    nextStep: (data) => stepAfterMedicalPlan(data),
    layout: 'full',
  },
  renewal_next_year_medical_plan_details: {
    component: RenewalNextMedicalPlanDetailPage,
    canShow: (data, metadata) => {
      return (
        data.missing_fields.plans_selection?.length === 0 &&
        metadata?.planIds?.length > 0 &&
        isRenewal(data)
      )
    },
    previousStep: () => null,
    nextStep: (data) => stepAfterMedicalPlan(data),
    layout: 'full',
  },
}

/* Logic needed to decide if the request step can actually be shown */
export const canShowThisStep = ({
  data,
  step,
  featureFlags,
}: {
  data: OnboardingInfo;
  step: Step;
  featureFlags?: { [key: string]: boolean };
}) => {
  return (
    componentMappings[getBaseStep(step)]?.canShow(
      data,
      getStepMetadata(step),
      featureFlags
    ) || false
  )
}

export const isThisStepCompleted = ({
  data,
  step,
  featureFlags,
}: {
  data: OnboardingInfo;
  step: Step;
  featureFlags?: { [key: string]: boolean };
}) => {
  return componentMappings[getBaseStep(step)]?.isCompleted
    ? componentMappings[getBaseStep(step)]?.isCompleted?.(data)
    : canShowThisStep({ data, step, featureFlags })
}
